import {ActionIcon, Badge, Button, Menu, Switch, Tooltip, useSafeMantineTheme} from '@mantine/core'
import {RichText} from 'components/form/RichText'
import {P, match} from 'ts-pattern'
import {ForecastLikelihoodEdit} from './ForecastLikelihoodEdit'
import {forecastCommentOnSave, ForecastFetched, UseForecastGroupApp} from './useForecasts'
import {
  MdOutlineClear,
  MdOutlineContentCopy,
  MdOutlineContentPaste,
  MdVisibility,
  MdVisibilityOff
} from 'react-icons/md'
import {StyleProps} from 'components'
import {keys, compactObj, deepEqual} from 'shared/collection'
import {notNull} from 'shared/type'
import {queryStringSerializer} from 'shared/queryStringSerializer'
import {DeltaValues} from 'shared/paths'
import {doSafe, sansOk} from 'shared/promise'
import {
  ForecastCfg,
  ForecastQuestionCfg,
  Logistic,
  ResolutionForecasts,
  zForecast,
  ForecastQuestionCfgAmount,
  ForecastLogistic
} from 'shared/forecast'
import {LabelEmptyList, SErrorBoundary, useMemoValue} from 'shared/ui'
import {Forecast as F} from 'shared/forecast/forecast'
import {OverflowMenu} from 'components/Menu'
import {HiArrowUturnLeft, HiTrash} from 'react-icons/hi2'
import {useShowHide} from 'components/Buttons'
import {useCallback, useContext, useEffect, useState} from 'react'
import {resultColorFn, ResultColorFn} from 'shared/forecast/results/QuestionResults'
import {useComments, UseComments, useCommentsQuery} from 'components/discussion/comments'
import {showError} from 'components/notify'
import {QuestionContext} from './questionComponentTypes'
import {AmountForecastFetched, ForecastAmount} from './ForecastAmount'
import {useCopy} from 'components/copy'
import {isEmpty, omit, pick, pipe} from 'remeda'
import {UseDbForm} from 'components/hooks/useDbForm'
import {questionStatusMd} from 'shared/forecast/questionStatus'
import {usePortal} from 'components/portal/portal'
import {TourId, tourTipProps} from 'components/portal/tourTypes'
import {TourInfo} from 'components/portal/Tour'
import {twMerge} from 'tailwind-merge'
import {ComparisonQForm, ComparisonSection} from './Comparison'
import {useQuestionQuery, useQuestionUIItem} from './useQuestions'

export type ForecastCommonProps = {
  onChange?: (forecast: ForecastCfg) => void
  color?: ResultColorFn
}

export type ForecastComponentResolution = Pick<
  ResolutionForecasts<ForecastFetched, UseForecastGroupApp>,
  | 'questionAgg'
  | 'baselineAgg'
  | 'forecasts'
  | 'group'
  | 'resolution'
  | 'resAgg'
  | 'code'
  | 'resolutionValue'
  | 'comparisons'
>

const zUiPath = F.zForecastGroupKey.pick({code: true, type: true, amountType: true}).strip()
const uiPathSerializer = queryStringSerializer(zUiPath)

export const Forecast = ({
  question,
  onChange,
  color: _color,
  resolution,
  className,
  qForm,
  showComparison
}: {
  question: ForecastQuestionCfg
  /** the question form for this forecasts question (not necessarily the root) */
  qForm: ComparisonQForm
  resolution: ForecastComponentResolution
  debugName?: string
  showComparison?: boolean
} & ForecastCommonProps &
  StyleProps) => {
  const {
    group: resGroup,
    baselineAgg,
    questionAgg,
    resAgg,
    code,
    resolutionValue,
    resolution: {enableForecasts}
  } = resolution
  const {save, group} = resGroup
  const myForecast = useMemoValue(resGroup.myForecast)
  const initial = pipe(
    myForecast?.cfg,
    f => (f && !f.deleted ? f : undefined),
    f =>
      f ??
      F.defaultForecast(
        match(question)
          .with(
            {forecastType: 'amount'},
            ({defaultBounds, amountType}) => ({type: 'logistic', code, bounds: defaultBounds, amountType}) as const
          )
          .with({forecastType: 'likelihood'}, q => ({type: 'likelihood', code}) as const)
          .exhaustive()
      ),
    f => ({...f, status: {}})
  )

  const subPath = [
    group.subquestion_id,
    uiPathSerializer.serialize(
      compactObj({
        code,
        type: question.forecastType,
        ...match(question)
          .with({forecastType: 'amount'}, ({amountType}) => ({
            amountType: amountType != 'number' ? amountType : undefined
          }))
          .otherwise(() => ({}))
      })
    )
  ].filter(notNull)

  const form = useQuestionUIItem({
    schema: zForecast,
    type: 'forecast',
    subPath,
    initial,
    onUpdate: ({cfg}) => onChange?.(cfg)
  })

  const isDraft = !deepEqual(compactObj(form.values), compactObj(initial))

  // if the question type changes, clear the form
  useEffect(() => {
    if (!F.isTypeCompatible(form.values.type, question.forecastType)) form.del()
  }, [question.forecastType])

  const forecast = useMemoValue(form.values)
  const showGroupForecast = useShowHide(myForecast != null)
  const [forecastLoading, setForecastLoading] = useState(false)
  const {colors} = useSafeMantineTheme()
  const color = _color ?? resultColorFn({colors, cfg: question.colors})
  const onNotesChanged = useCallback(
    (md: string) => {
      form.setFieldValue('notes', md)
    },
    [form.setFieldValue]
  )

  const [postState, setPostState] = useState<boolean | undefined>(undefined)
  const postComment = postState ?? (isDraft && !isEmpty(forecast.notes))

  const {question_id, isOwner} = useContext(QuestionContext)!

  const uCopy = useCopy({schema: zForecast, type: `forecast-${forecast.type}`})
  const pasteValid = uCopy.pasteValue && F.isTypeCompatible(uCopy.pasteValue.type, question.forecastType)

  const {profile, readonly} = usePortal()
  const rootQuestion = pipe(useQuestionQuery({question_id, pick: q => pick(q.cfg, ['status'])}).data, d =>
    d?.ok ? sansOk(d) : undefined
  )
  const readonlyReason =
    readonly ? 'This workspace is read only'
    : !profile.hasPermission('forecast') ? 'no permission to forecast'
    : resolutionValue != null && resolutionValue.status != 'pending' ?
      'This forecast has been resolved - no new forecasts allowed'
    : doSafe(rootQuestion?.status, s =>
        questionStatusMd[s].forecast?.readonly ? `The main question is ${s} and not accepting forecasts` : undefined
      )

  const editCommon = {questionAgg, showGroup: showGroupForecast.show, color, resolution}

  const uComments = useComments({
    question_id,
    subquestion_id: group.subquestion_id,
    created_by: profile.id,
    disabled: readonly
  })

  return (
    (enableForecasts || showComparison) && (
      <div
        className={twMerge(`relative space-y-4`, className)}
        data-test-id={`forecast-${[group.subquestion_id ?? 'question', code].filter(notNull)}`}
        {...tourTipProps(
          match(forecast.type)
            .with('logistic', 'drawn', () => 'forecast-amount' as const)
            .with('likelihood', () => 'forecast-likelihood' as const)
            .exhaustive()
        )}>
        <SErrorBoundary>
          {showComparison && (
            <ComparisonSection
              className='p-3 border rounded-lg'
              uProps={{qForm, comparisonKey: resGroup.group}}
              mode='existing'
            />
          )}
        </SErrorBoundary>

        {enableForecasts ?
          <>
            {match(forecast)
              .with({type: P.union('logistic', 'drawn')}, f => (
                <ForecastAmount
                  forecast={f}
                  {...editCommon}
                  question={question as ForecastQuestionCfgAmount}
                  onDelta={v => form.setValues(v)}
                  previousForecast={myForecast as AmountForecastFetched}
                />
              ))
              .with({type: 'likelihood'}, l => (
                <ForecastLikelihoodEdit
                  {...editCommon}
                  value={pick(l, ['likelihood', 'relativePoints'])}
                  baselineAgg={baselineAgg}
                  onChange={v => form.setValues(v as DeltaValues<ForecastCfg>)}
                  aggForecast={resAgg}
                  myForecast={match(myForecast?.cfg)
                    .with({type: 'likelihood'}, l => l)
                    .otherwise(() => undefined)}
                  color={color}
                />
              ))
              .exhaustive()}
            <div className='flex flex-col gap-2 pt-2'>
              {forecast && (
                <RichText
                  className='grow min-h-[3em]'
                  value={forecast.notes ?? ''}
                  data-test-id='forecast-note'
                  placeholder='Notes about your forecast (shown in the results viz)'
                  onChange={onNotesChanged}
                  debugTitle={group.subquestion_id}
                />
              )}

              <div className='flex items-center justify-end gap-4'>
                {isDraft && (
                  <div className='flex items-center'>
                    <Badge variant='outline' color='orange'>
                      draft
                    </Badge>
                    <Tooltip label='Cancel draft changes'>
                      <div>
                        <ActionIcon component='div' onClick={() => form.del()}>
                          <MdOutlineClear size={18} style={{color: colors.orange?.[5]}} />
                        </ActionIcon>
                      </div>
                    </Tooltip>
                  </div>
                )}
                {isDraft && (
                  <Tooltip label={`Toggle if the forecast will also be posted as comment`}>
                    <div data-test-id='post-comment'>
                      <Switch
                        size='xl'
                        onLabel='post comment'
                        offLabel='no comment'
                        styles={{trackLabel: {padding: '0 0.8em'}}}
                        checked={postComment}
                        onChange={c => setPostState(c.target.checked)}
                      />
                    </div>
                  </Tooltip>
                )}

                {myForecast && <ForecastStatus status={myForecast.cfg.status} />}
                <Tooltip
                  label={
                    readonlyReason ? readonlyReason
                    : myForecast ?
                      'Update forecast'
                    : 'Submit forecast'
                  }>
                  <div>
                    <Button
                      variant='filled'
                      disabled={readonlyReason != null || !isDraft}
                      loading={forecastLoading}
                      data-test-id='forecast-submit'
                      {...tourTipProps('forecast-submit', !isOwner)}
                      onClick={async () => {
                        if (!forecast) return
                        setForecastLoading(true)
                        await save(
                          forecast.type == 'logistic' ?
                            Logistic.forecastWithDefaults(forecast, question as ForecastQuestionCfgAmount)
                          : forecast,
                          {post: postComment, previous: myForecast?.cfg}
                        )
                          .then(async f => {
                            form.del()
                            if (!postComment || !uComments || !f) return
                            await forecastCommentOnSave({
                              newId: f.id,
                              question_id,
                              uComments,
                              cfg: forecast,
                              resolution: {...resolution, cfg: question}
                            }).catch(e => {
                              showError(e, 'Failed to post forecast comment')
                            })
                          })
                          .catch(e => {
                            showError(e, 'Failed to save forecast')
                          })

                        setPostState(undefined)
                        setForecastLoading(false)
                      }}
                      size='sm'>
                      Forecast
                    </Button>
                  </div>
                </Tooltip>
                <OverflowMenu>
                  {myForecast != null && (
                    <Menu.Item
                      leftSection={<HiTrash />}
                      onClick={() => {
                        save({...myForecast.cfg, deleted: true})
                        form.del()
                      }}>
                      Remove my forecast
                    </Menu.Item>
                  )}
                  <Menu.Item
                    leftSection={<MdOutlineContentCopy />}
                    disabled={!forecast}
                    onClick={() => {
                      uCopy.copy(forecast)
                    }}>
                    Copy
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<MdOutlineContentPaste />}
                    disabled={!pasteValid}
                    onClick={() => {
                      form.setValues({
                        ...form.values,
                        ...omit(uCopy.pasteValue as any, ['notes', 'bounds', 'code', 'status', 'deleted'])
                      })
                      uCopy.clear()
                    }}>
                    Paste
                  </Menu.Item>
                  <Menu.Item
                    leftSection={showGroupForecast.show ? <MdVisibilityOff /> : <MdVisibility />}
                    onClick={() => {
                      showGroupForecast.setShow(!showGroupForecast.show)
                    }}>
                    {showGroupForecast.show ? 'Hide group forecast' : 'Show group forecast'}
                  </Menu.Item>
                  {match(forecast as ForecastCfg)
                    .with({type: 'logistic'}, l => {
                      if (deepEqual(l.bounds, (question as ForecastQuestionCfgAmount).defaultBounds)) return <></>
                      return (
                        <Menu.Item
                          leftSection={<HiArrowUturnLeft />}
                          onClick={() =>
                            (form as any as UseDbForm<ForecastLogistic>).setFieldValue(
                              'bounds',
                              (question as ForecastQuestionCfgAmount).defaultBounds
                            )
                          }>
                          Reset to question bounds
                        </Menu.Item>
                      )
                    })
                    .otherwise(() => (
                      <></>
                    ))}
                </OverflowMenu>
                <TourInfo
                  tips={[
                    'forecast-submit',
                    ...match(forecast.type)
                      .with('logistic', 'drawn', f => ['forecast-amount', 'forecast-draw'] as TourId[])
                      .with('likelihood', () => ['forecast-likelihood' as const])
                      .otherwise(() => [])
                  ]}
                />
              </div>
            </div>
          </>
        : <></>}

        {enableForecasts === false ?
          <LabelEmptyList>Forecasts disabled</LabelEmptyList>
        : <></>}
      </div>
    )
  )
}

export const ForecastStatus = ({status}: Pick<ForecastCfg, 'status'>) =>
  keys(status).map(s => (
    <Tooltip key={s} label='This forecast will not be included in the results. Re-forecast for it to be shown'>
      <Badge variant='outline' color='red'>
        {s}
      </Badge>
    </Tooltip>
  ))
